/**
 * TutorialManager.ts - Manages tutorial state and progression
 */

export type TutorialState = 
  | 'WELCOME'
  | 'TUTORIAL_OBSERVE'
  | 'TUTORIAL_REPEAT'
  | 'TUTORIAL_ORGANIC'
  | 'TUTORIAL_AUDIO'
  | 'TUTORIAL_COMPLETE'
  // Chaos Tutorial
  | 'CHAOS_TUTORIAL_INTRUDERS'
  | 'CHAOS_TUTORIAL_PHYSICS'
  | 'CHAOS_TUTORIAL_INVISIBLE'
  | 'CHAOS_TUTORIAL_COMPLETE'
  // Rhythm Tutorial
  | 'RHYTHM_TUTORIAL_TIMING'
  | 'RHYTHM_TUTORIAL_WEAR'
  | 'RHYTHM_TUTORIAL_PANIC'
  | 'RHYTHM_TUTORIAL_COMPLETE';

export interface TutorialStep {
  state: TutorialState;
  title: string;
  message: string;
  sequence: number[];
  allowInput: boolean;
  showContinue: boolean;
  audioOnly?: boolean;
  showReorganization?: boolean;
}

export const TUTORIAL_STEPS: Record<TutorialState, TutorialStep | null> = {
  'WELCOME': null, // Handled by WelcomeScreen
  'TUTORIAL_OBSERVE': {
    state: 'TUTORIAL_OBSERVE',
    title: 'Paso 1: Observar',
    message: 'Simon muestra una secuencia. Observa y memoriza.',
    sequence: [0, 2],
    allowInput: false,
    showContinue: true,
  },
  'TUTORIAL_REPEAT': {
    state: 'TUTORIAL_REPEAT',
    title: 'Paso 2: Repetir',
    message: 'Ahora repite la secuencia tocando los botones en el mismo orden.',
    sequence: [0, 2],
    allowInput: true,
    showContinue: false,
  },
  'TUTORIAL_ORGANIC': {
    state: 'TUTORIAL_ORGANIC',
    title: 'Paso 3: Tablero Orgánico',
    message: 'En Simon Revolutions, los botones cambian de posición entre rondas. ¡Fíjate bien!',
    sequence: [1, 3],
    allowInput: true,
    showContinue: false,
    showReorganization: true,
  },
  'TUTORIAL_AUDIO': {
    state: 'TUTORIAL_AUDIO',
    title: 'Paso 4: Audio Posicional',
    message: 'Cierra los ojos y escucha. El audio te indica dónde está cada botón.',
    sequence: [0, 3, 1],
    allowInput: true,
    showContinue: false,
    audioOnly: false, // Toggle-able
  },
  'TUTORIAL_COMPLETE': {
    state: 'TUTORIAL_COMPLETE',
    title: '¡Listo para Jugar!',
    message: '¡Ya estás listo para jugar! Elige un modo y demuestra tu memoria.',
    sequence: [],
    allowInput: false,
    showContinue: true,
  },
  // Chaos Tutorial (placeholders for future tasks)
  'CHAOS_TUTORIAL_INTRUDERS': null,
  'CHAOS_TUTORIAL_PHYSICS': null,
  'CHAOS_TUTORIAL_INVISIBLE': null,
  'CHAOS_TUTORIAL_COMPLETE': null,
  // Rhythm Tutorial (placeholders for future tasks)
  'RHYTHM_TUTORIAL_TIMING': null,
  'RHYTHM_TUTORIAL_WEAR': null,
  'RHYTHM_TUTORIAL_PANIC': null,
  'RHYTHM_TUTORIAL_COMPLETE': null,
};

export class TutorialManager {
  private currentState: TutorialState = 'TUTORIAL_OBSERVE';
  private userAttempt: number[] = [];
  private currentSequence: number[] = [];
  private audioOnlyMode: boolean = false;
  private blinkTarget: number | null = null;

  getCurrentState(): TutorialState {
    return this.currentState;
  }

  getCurrentStep(): TutorialStep | null {
    return TUTORIAL_STEPS[this.currentState];
  }

  setAudioOnlyMode(enabled: boolean): void {
    this.audioOnlyMode = enabled;
  }

  getAudioOnlyMode(): boolean {
    return this.audioOnlyMode;
  }

  startStep(state: TutorialState): void {
    this.currentState = state;
    const step = TUTORIAL_STEPS[state];
    if (step) {
      this.currentSequence = [...step.sequence];
      this.userAttempt = [];
    }
  }

  recordInput(buttonIndex: number): 'correct' | 'incorrect' | 'complete' {
    this.userAttempt.push(buttonIndex);
    
    const expectedIndex = this.userAttempt.length - 1;
    
    // Check if current input is correct
    if (this.currentSequence[expectedIndex] !== buttonIndex) {
      // Incorrect input - reset attempt and blink
      this.userAttempt = [];
      this.updateBlinkTarget();
      return 'incorrect';
    }
    
    // Check if sequence is complete
    if (this.userAttempt.length === this.currentSequence.length) {
      this.stopBlinking();
      return 'complete';
    }
    
    // Correct but not complete - advance blink to next button
    this.updateBlinkTarget();
    return 'correct';
  }

  resetAttempt(): void {
    this.userAttempt = [];
  }

  advance(): TutorialState | null {
    const stateOrder: TutorialState[] = [
      'TUTORIAL_OBSERVE',
      'TUTORIAL_REPEAT',
      'TUTORIAL_ORGANIC',
      'TUTORIAL_AUDIO',
      'TUTORIAL_COMPLETE',
    ];

    const currentIndex = stateOrder.indexOf(this.currentState);
    if (currentIndex >= 0 && currentIndex < stateOrder.length - 1) {
      this.currentState = stateOrder[currentIndex + 1];
      this.startStep(this.currentState);
      return this.currentState;
    }

    return null; // Tutorial complete
  }

  getUserAttempt(): number[] {
    return [...this.userAttempt];
  }

  getCurrentSequence(): number[] {
    return [...this.currentSequence];
  }

  getBlinkTarget(): number | null {
    return this.blinkTarget;
  }

  startBlinking(): void {
    // Start blinking the first button in the sequence
    this.updateBlinkTarget();
  }

  stopBlinking(): void {
    this.blinkTarget = null;
  }

  updateBlinkTarget(): void {
    // Set blink target to the next expected button in the sequence
    if (this.userAttempt.length < this.currentSequence.length) {
      this.blinkTarget = this.currentSequence[this.userAttempt.length];
    } else {
      this.blinkTarget = null;
    }
  }
}
